/**
 * Evidence Vault Service
 *
 * Handles real Firebase Storage uploads and Firestore document management
 * for the Evidence Vault feature.
 */

import { db, storage, auth } from "@/lib/firebase";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  QueryConstraint,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { READ_MODE, ENABLE_DUAL_WRITE, dualWriteJobDocument, dualReadJobDocuments } from "@/lib/firestore-helpers";
import {
  EvidenceDocument,
  EvidenceDocumentType,
  EvidenceDocumentStatus,
  EvidenceVaultStats,
  RejectionNotice,
  DocumentVersion,
} from "@/services/mock/evidenceVaultData";

// ============================================
// EVIDENCE DOCUMENTS
// ============================================

/**
 * Upload an evidence document to Firebase Storage and create Firestore record
 */
export async function uploadEvidenceDocument(
  contractorId: string,
  file: File,
  type: EvidenceDocumentType,
  name: string,
  options?: {
    linkedJobId?: string;
    linkedJobAddress?: string;
    expirationDate?: string;
  }
): Promise<EvidenceDocument> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");

  // Generate document ID
  const documentId = doc(collection(db, "evidence_documents")).id;
  const timestamp = Timestamp.now();

  // Upload file to Firebase Storage
  const storagePath = `contractors/${contractorId}/evidence/${type}/${documentId}/${file.name}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);

  // Get user's display name
  const userDoc = await getDoc(doc(db, "users", user.uid));
  const userData = userDoc.data();
  const uploadedBy = userData?.fullName || userData?.full_name || user.email || "Unknown";

  // Create version record
  const versionId = `v1-${Date.now()}`;
  const version: DocumentVersion = {
    id: versionId,
    versionNumber: 1,
    fileName: file.name,
    fileSize: file.size,
    uploadedBy,
    uploadedAt: timestamp.toDate().toISOString(),
  };

  // Create evidence document data
  const evidenceData: Omit<EvidenceDocument, "id"> = {
    name,
    type,
    fileName: file.name,
    fileSize: file.size,
    filePath: downloadURL,
    linkedJobIds: options?.linkedJobId ? [options.linkedJobId] : [],
    linkedJobAddresses: options?.linkedJobAddress ? [options.linkedJobAddress] : [],
    linkedRequirements: [],
    satisfiesRequirementCount: 0,
    totalLinkedRequirements: 0,
    currentVersion: 1,
    versions: [version],
    status: "needs_review" as EvidenceDocumentStatus,
    expirationDate: options?.expirationDate,
    uploadedBy,
    uploadedAt: timestamp.toDate().toISOString(),
    lastUpdated: timestamp.toDate().toISOString(),
  };

  // Write to Firestore using dual-write pattern
  await dualWriteEvidenceDocument(contractorId, documentId, evidenceData);

  return {
    id: documentId,
    ...evidenceData,
  };
}

/**
 * Create a new version of an existing evidence document
 */
export async function createEvidenceDocumentVersion(
  contractorId: string,
  documentId: string,
  file: File,
  changeDescription?: string
): Promise<DocumentVersion> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");

  // Get existing document
  const existingDoc = await dualReadEvidenceDocument(contractorId, documentId);
  if (!existingDoc) throw new Error("Document not found");

  const timestamp = Timestamp.now();
  const newVersionNumber = existingDoc.currentVersion + 1;

  // Upload new file version
  const storagePath = `contractors/${contractorId}/evidence/${existingDoc.type}/${documentId}/v${newVersionNumber}/${file.name}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);

  // Get user's display name
  const userDoc = await getDoc(doc(db, "users", user.uid));
  const userData = userDoc.data();
  const uploadedBy = userData?.fullName || userData?.full_name || user.email || "Unknown";

  // Create new version record
  const versionId = `v${newVersionNumber}-${Date.now()}`;
  const newVersion: DocumentVersion = {
    id: versionId,
    versionNumber: newVersionNumber,
    fileName: file.name,
    fileSize: file.size,
    uploadedBy,
    uploadedAt: timestamp.toDate().toISOString(),
    changeDescription,
  };

  // Update document with new version
  const updatedVersions = [...existingDoc.versions, newVersion];

  await dualWriteEvidenceDocument(contractorId, documentId, {
    currentVersion: newVersionNumber,
    versions: updatedVersions,
    filePath: downloadURL,
    fileName: file.name,
    fileSize: file.size,
    lastUpdated: timestamp.toDate().toISOString(),
  });

  return newVersion;
}

/**
 * Get all evidence documents for a contractor
 */
export async function getEvidenceDocuments(
  contractorId: string,
  constraints: QueryConstraint[] = []
): Promise<EvidenceDocument[]> {
  const snapshot = await dualReadEvidenceDocuments(contractorId, constraints);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as EvidenceDocument[];
}

/**
 * Get a single evidence document
 */
export async function getEvidenceDocument(
  contractorId: string,
  documentId: string
): Promise<EvidenceDocument | null> {
  return dualReadEvidenceDocument(contractorId, documentId);
}

/**
 * Update an evidence document's metadata
 */
export async function updateEvidenceDocument(
  contractorId: string,
  documentId: string,
  updates: Partial<EvidenceDocument>
): Promise<void> {
  const timestamp = Timestamp.now();
  await dualWriteEvidenceDocument(contractorId, documentId, {
    ...updates,
    lastUpdated: timestamp.toDate().toISOString(),
  });
}

/**
 * Delete an evidence document
 */
export async function deleteEvidenceDocument(
  contractorId: string,
  documentId: string
): Promise<void> {
  // Get document to find storage path
  const existingDoc = await dualReadEvidenceDocument(contractorId, documentId);
  if (!existingDoc) throw new Error("Document not found");

  // Delete from storage (all versions)
  for (const version of existingDoc.versions) {
    try {
      const storagePath = `contractors/${contractorId}/evidence/${existingDoc.type}/${documentId}/v${version.versionNumber}/${version.fileName}`;
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);
    } catch (error) {
      console.warn(`Failed to delete version ${version.versionNumber} from storage:`, error);
    }
  }

  // Delete from Firestore
  await dualDeleteEvidenceDocument(contractorId, documentId);
}

/**
 * Link an evidence document to a job
 */
export async function linkDocumentToJob(
  contractorId: string,
  documentId: string,
  jobId: string,
  jobAddress?: string
): Promise<void> {
  const existingDoc = await dualReadEvidenceDocument(contractorId, documentId);
  if (!existingDoc) throw new Error("Document not found");

  const updatedJobIds = [...new Set([...existingDoc.linkedJobIds, jobId])];
  const updatedAddresses = existingDoc.linkedJobAddresses
    ? [...new Set([...existingDoc.linkedJobAddresses, jobAddress].filter(Boolean))]
    : jobAddress ? [jobAddress] : undefined;

  await dualWriteEvidenceDocument(contractorId, documentId, {
    linkedJobIds: updatedJobIds,
    linkedJobAddresses: updatedAddresses as string[] | undefined,
    lastUpdated: Timestamp.now().toDate().toISOString(),
  });
}

/**
 * Get evidence vault statistics
 */
export async function getEvidenceVaultStats(
  contractorId: string
): Promise<EvidenceVaultStats> {
  const documents = await getEvidenceDocuments(contractorId);

  const stats: EvidenceVaultStats = {
    totalDocuments: documents.length,
    byType: {
      permit_form: 0,
      plan_set: 0,
      calculation: 0,
      license: 0,
      insurance: 0,
      product_approval: 0,
      authorization: 0,
      inspection_evidence: 0,
      city_correspondence: 0,
      rejection_notice: 0,
      affidavit: 0,
      specification: 0,
      other: 0,
    },
    byStatus: {
      required: 0,
      optional: 0,
      satisfies_requirement: 0,
      needs_review: 0,
      expired: 0,
      rejected: 0,
    },
    linkedToJobs: 0,
    expiringWithin30Days: 0,
    needsReview: 0,
  };

  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

  for (const doc of documents) {
    // Count by type
    if (stats.byType[doc.type] !== undefined) {
      stats.byType[doc.type]++;
    }

    // Count by status
    if (stats.byStatus[doc.status] !== undefined) {
      stats.byStatus[doc.status]++;
    }

    // Count linked to jobs
    if (doc.linkedJobIds.length > 0) {
      stats.linkedToJobs++;
    }

    // Count needs review
    if (doc.status === "needs_review") {
      stats.needsReview++;
    }

    // Count expiring within 30 days
    if (doc.expirationDate) {
      const expirationDate = new Date(doc.expirationDate);
      if (expirationDate <= thirtyDaysFromNow && expirationDate > new Date()) {
        stats.expiringWithin30Days++;
      }
    }
  }

  return stats;
}

/**
 * Get rejection notices for a contractor
 */
export async function getRejectionNotices(
  contractorId: string
): Promise<RejectionNotice[]> {
  // Get documents of type rejection_notice
  const documents = await getEvidenceDocuments(
    contractorId,
    [where("type", "==", "rejection_notice")]
  );

  // Transform to rejection notices format
  // For now, return empty array since parsing is a future feature
  return [];
}

// ============================================
// REUSABLE DOCUMENTS FOR JOB IMPORT
// ============================================

/** Document types that are reusable across jobs */
const REUSABLE_DOCUMENT_TYPES: EvidenceDocumentType[] = [
  'license',
  'insurance',
  'authorization',
  'product_approval',
];

/**
 * Get evidence documents suitable for importing into jobs
 * Filters to reusable types (licenses, insurance, certifications)
 * and excludes expired/rejected documents
 */
export async function getReusableEvidenceDocuments(
  contractorId: string
): Promise<EvidenceDocument[]> {
  const allDocs = await getEvidenceDocuments(contractorId);
  return allDocs.filter(doc =>
    REUSABLE_DOCUMENT_TYPES.includes(doc.type) &&
    doc.status !== 'expired' &&
    doc.status !== 'rejected'
  );
}

/**
 * Map evidence document type to job document category
 */
function mapEvidenceTypeToCategory(type: EvidenceDocumentType): string | null {
  const mapping: Record<string, string> = {
    'license': 'contractor_license',
    'insurance': 'insurance_coi',
    'authorization': 'owner_auth',
    'product_approval': 'product_approval',
  };
  return mapping[type] || null;
}

/**
 * Link an evidence vault document to a job
 * - Updates the evidence document's linkedJobIds
 * - Creates a reference in the job's documents collection
 */
export async function linkVaultDocumentToJob(
  contractorId: string,
  evidenceDocument: EvidenceDocument,
  jobId: string,
  jobAddress: string
): Promise<void> {
  // 1. Update the evidence document's linkedJobIds
  await linkDocumentToJob(contractorId, evidenceDocument.id, jobId, jobAddress);

  // 2. Create a reference in job_documents collection
  const docId = doc(collection(db, 'job_documents')).id;
  const category = mapEvidenceTypeToCategory(evidenceDocument.type);

  await dualWriteJobDocument(contractorId, jobId, docId, {
    document_name: evidenceDocument.name,
    document_url: evidenceDocument.filePath,
    document_type: 'certificate',
    description: `Imported from Evidence Vault`,
    category: category,
    complianceCategory: category,
    status: 'uploaded',
    evidenceVaultDocumentId: evidenceDocument.id,
    sourceType: 'evidence_vault',
    createdAt: Timestamp.now(),
  });
}

/**
 * Check which essential documents exist in the vault
 * Returns status for: license, insurance (COI), workers_comp
 */
export async function getEssentialDocumentsStatus(
  contractorId: string
): Promise<{
  license: boolean;
  insurance: boolean;
  workersComp: boolean;
  total: number;
  completed: number;
}> {
  const documents = await getEvidenceDocuments(contractorId);

  const hasLicense = documents.some(
    d => d.type === 'license' && d.status !== 'expired' && d.status !== 'rejected'
  );
  const hasInsurance = documents.some(
    d => d.type === 'insurance' && d.status !== 'expired' && d.status !== 'rejected'
  );
  // Workers comp is stored as insurance type but we can check name/description
  const hasWorkersComp = documents.some(
    d => d.type === 'insurance' &&
      d.status !== 'expired' &&
      d.status !== 'rejected' &&
      (d.name.toLowerCase().includes('workers') || d.name.toLowerCase().includes('comp'))
  );

  const completed = [hasLicense, hasInsurance, hasWorkersComp].filter(Boolean).length;

  return {
    license: hasLicense,
    insurance: hasInsurance,
    workersComp: hasWorkersComp,
    total: 3,
    completed,
  };
}

// ============================================
// AUTO-ARCHIVE ON JOB SUBMISSION
// ============================================

/**
 * Map job document category to evidence document type
 */
function mapCategoryToEvidenceType(category: string | null): EvidenceDocumentType {
  const mapping: Record<string, EvidenceDocumentType> = {
    'contractor_license': 'license',
    'insurance_coi': 'insurance',
    'owner_auth': 'authorization',
    'product_approval': 'product_approval',
    'plans': 'plan_set',
    'calculations': 'calculations',
    'permit_application': 'permit_package',
  };
  return mapping[category || ''] || 'required_attachment';
}

/**
 * Archive job documents to Evidence Vault when job is submitted for inspection.
 * - Skips documents that already came from Evidence Vault (have evidenceVaultDocumentId or sourceType: 'evidence_vault')
 * - Creates Evidence Vault entries for remaining documents (plans, calculations, etc.)
 * - Links them to the job for audit trail
 */
export async function archiveJobDocumentsToVault(
  contractorId: string,
  jobId: string,
  jobAddress: string
): Promise<{ archived: number; skipped: number }> {
  // 1. Get all job documents
  const jobDocsSnapshot = await dualReadJobDocuments(contractorId, jobId);
  const jobDocs = jobDocsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

  let archived = 0;
  let skipped = 0;

  for (const jobDoc of jobDocs) {
    // 2. Skip documents that already came from Evidence Vault
    if (jobDoc.evidenceVaultDocumentId || jobDoc.sourceType === 'evidence_vault') {
      skipped++;
      continue;
    }

    // 3. Skip if no URL (can't archive without file)
    if (!jobDoc.document_url) {
      continue;
    }

    // 4. Check if this document URL already exists in Evidence Vault (by file path)
    const existingDocs = await getEvidenceDocuments(contractorId);
    const alreadyExists = existingDocs.some(
      ed => ed.filePath === jobDoc.document_url
    );
    if (alreadyExists) {
      skipped++;
      continue;
    }

    // 5. Create Evidence Vault entry
    const evidenceDocId = doc(collection(db, 'evidence_documents')).id;
    const evidenceType = mapCategoryToEvidenceType(jobDoc.category || jobDoc.complianceCategory);

    const evidenceDocData: Partial<EvidenceDocument> = {
      id: evidenceDocId,
      name: jobDoc.document_name || 'Archived Document',
      fileName: jobDoc.document_name || 'document',
      type: evidenceType,
      status: 'satisfies_requirement' as EvidenceDocumentStatus,
      filePath: jobDoc.document_url,
      fileSize: 0, // We don't have this from job_documents
      mimeType: 'application/octet-stream',
      linkedJobIds: [jobId],
      linkedJobAddresses: [jobAddress],
      uploadedBy: 'system',
      uploadedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      currentVersion: 1,
      versions: [{
        id: `${evidenceDocId}-v1`,
        versionNumber: 1,
        fileName: jobDoc.document_name || 'document',
        fileSize: 0,
        filePath: jobDoc.document_url,
        uploadedBy: 'system',
        uploadedAt: new Date().toISOString(),
      }],
      linkedRequirements: [],
      totalLinkedRequirements: 0,
      satisfiesRequirementCount: 0,
      archivedFromJob: jobId,
      archivedAt: new Date().toISOString(),
    };

    await dualWriteEvidenceDocument(contractorId, evidenceDocId, evidenceDocData);

    // 6. Update job document to link back to Evidence Vault
    await dualWriteJobDocument(contractorId, jobId, jobDoc.id, {
      evidenceVaultDocumentId: evidenceDocId,
      archivedToVaultAt: new Date().toISOString(),
    });

    archived++;
  }

  return { archived, skipped };
}

// ============================================
// DUAL-WRITE HELPERS
// ============================================

// Remove undefined values from an object (Firestore doesn't accept undefined)
function removeUndefinedFields(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  );
}

async function dualWriteEvidenceDocument(
  contractorId: string,
  documentId: string,
  data: any
) {
  const timestamp = Timestamp.now();
  const documentData = removeUndefinedFields({
    ...data,
    updatedAt: timestamp,
    ...(!data.createdAt && { createdAt: timestamp }),
  });

  // Write to new structure
  await setDoc(
    doc(db, `contractors/${contractorId}/evidence_documents/${documentId}`),
    documentData,
    { merge: true }
  );

  // Write to old structure for dual-write
  if (ENABLE_DUAL_WRITE) {
    await setDoc(
      doc(db, `evidence_documents/${documentId}`),
      { ...documentData, contractorId },
      { merge: true }
    );
  }
}

async function dualReadEvidenceDocuments(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === "new") {
    const q = query(
      collection(db, `contractors/${contractorId}/evidence_documents`),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, "evidence_documents"),
      where("contractorId", "==", contractorId),
      ...constraints
    );
    return getDocs(q);
  }
}

async function dualReadEvidenceDocument(
  contractorId: string,
  documentId: string
): Promise<EvidenceDocument | null> {
  let docRef;
  if (READ_MODE === "new") {
    docRef = doc(db, `contractors/${contractorId}/evidence_documents/${documentId}`);
  } else {
    docRef = doc(db, `evidence_documents/${documentId}`);
  }

  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as EvidenceDocument;
}

async function dualDeleteEvidenceDocument(
  contractorId: string,
  documentId: string
) {
  // Delete from new structure
  await deleteDoc(doc(db, `contractors/${contractorId}/evidence_documents/${documentId}`));

  // Delete from old structure
  if (ENABLE_DUAL_WRITE) {
    await deleteDoc(doc(db, `evidence_documents/${documentId}`));
  }
}
